import { type ReactNode, useEffect, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import DashboardLayout from '@/components/DashboardLayout'
import CampaignWorkspace from '@/components/CampaignWorkspace'
import { Ticket, Megaphone, Newspaper, Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import type { SalesCategory, SalesGranularity, SalesSeriesPoint, SalesSummary } from '@/types'

type DashboardEventShowProps = {
  event: {
    id: string
    slug: string
    title: string
    date: string | null
    status: 'draft' | 'published'
    page_kind: 'event' | 'fundraiser'
  }
  sales: SalesSummary
  sales_series: SalesSeriesPoint[]
  sales_from: string
  sales_to: string
  sales_granularity: SalesGranularity
  landing_url: string | null
}

const chartConfig = {
  tickets: { label: 'Tickets', color: 'var(--chart-1)' },
  sponsorships: { label: 'Sponsors', color: 'var(--chart-2)' },
  ads: { label: 'Ads', color: 'var(--chart-3)' },
  donations: { label: 'Donations', color: 'var(--chart-4)' },
} satisfies ChartConfig

type CategoryKey = keyof typeof chartConfig

const TODAY = new Date().toISOString().slice(0, 10)

function formatMoney(cents: number) {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function formatAxisMoney(cents: number) {
  const dollars = cents / 100
  if (Math.abs(dollars) >= 1000) return `$${(dollars / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`
  return `$${dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

function parseUTC(date: string) {
  return new Date(`${date}T00:00:00Z`)
}

function formatTick(date: string, granularity: SalesGranularity) {
  const opts: Intl.DateTimeFormatOptions =
    granularity === 'month'
      ? { month: 'short', timeZone: 'UTC' }
      : { month: 'short', day: 'numeric', timeZone: 'UTC' }
  return parseUTC(date).toLocaleDateString('en-US', opts)
}

function formatFullDate(date: string, granularity: SalesGranularity) {
  const opts: Intl.DateTimeFormatOptions =
    granularity === 'month'
      ? { month: 'long', year: 'numeric', timeZone: 'UTC' }
      : { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }
  return parseUTC(date).toLocaleDateString('en-US', opts)
}

type SalesCardProps = {
  label: string
  icon: ReactNode
  category: SalesCategory
  unitLabel: string
}

function SalesCard({ label, icon, category, unitLabel }: SalesCardProps) {
  return (
    <div className="rounded-xl border border-black/10 bg-card p-4 shadow-xs">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-base font-medium">{label}</span>
      </div>
      <p className="mt-2 text-3xl font-semibold tabular-nums">{formatMoney(category.total_cents)}</p>
      <p className="mt-1 text-sm text-muted-foreground tabular-nums">
        {category.count.toLocaleString()} {unitLabel}
      </p>
    </div>
  )
}

function SalesTrendChart({
  series,
  fromDate,
  toDate,
  granularity,
  isFundraiser,
}: {
  series: SalesSeriesPoint[]
  fromDate: string
  toDate: string
  granularity: SalesGranularity
  isFundraiser: boolean
}) {
  const [from, setFrom] = useState(fromDate)
  const [to, setTo] = useState(toDate)
  const [category, setCategory] = useState<CategoryKey | 'all'>('all')
  const [loading, setLoading] = useState(false)

  // Keep inputs in sync with the server-sanitized range (it may swap/clamp).
  useEffect(() => {
    setFrom(fromDate)
    setTo(toDate)
  }, [fromDate, toDate])

  const categories: CategoryKey[] = isFundraiser
    ? ['donations']
    : ['tickets', 'sponsorships', 'ads', 'donations']
  const visibleCategories: CategoryKey[] =
    category === 'all' ? categories : [category]

  const hasData = series.some((p) => visibleCategories.reduce((sum, key) => sum + p[key], 0) > 0)

  function applyRange(nextFrom: string, nextTo: string) {
    setFrom(nextFrom)
    setTo(nextTo)
    if (!nextFrom || !nextTo) return
    router.reload({
      only: ['sales_series', 'sales_from', 'sales_to', 'sales_granularity'],
      data: { from: nextFrom, to: nextTo },
      onStart: () => setLoading(true),
      onFinish: () => setLoading(false),
    })
  }

  return (
    <div className="mt-8 rounded-2xl border border-black/10 bg-card p-4 shadow-xs sm:p-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Sales over time</h2>
          <p className="text-sm text-muted-foreground">Net revenue by category</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {!isFundraiser && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as CategoryKey | 'all')}>
                <SelectTrigger className="w-[9.5rem]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" align="end">
                  <SelectGroup>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((key) => (
                      <SelectItem key={key} value={key}>
                        {chartConfig[key].label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="sales-from" className="text-xs text-muted-foreground">From</Label>
            <Input
              id="sales-from"
              type="date"
              value={from}
              max={to || TODAY}
              className="w-[9.5rem]"
              onChange={(e) => applyRange(e.target.value, to)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sales-to" className="text-xs text-muted-foreground">To</Label>
            <Input
              id="sales-to"
              type="date"
              value={to}
              min={from || undefined}
              max={TODAY}
              className="w-[9.5rem]"
              onChange={(e) => applyRange(from, e.target.value)}
            />
          </div>
        </div>
      </div>

      {hasData ? (
        <ChartContainer
          config={chartConfig}
          className={`h-[280px] w-full transition-opacity ${loading ? 'opacity-50' : 'opacity-100'}`}
        >
          <AreaChart data={series} margin={{ left: 4, right: 8, top: 8 }}>
            <defs>
              {visibleCategories.map((key) => (
                <linearGradient key={key} id={`fill-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={`var(--color-${key})`} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={`var(--color-${key})`} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={28}
              tickFormatter={(value: string) => formatTick(value, granularity)}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={52}
              tickFormatter={(value: number) => formatAxisMoney(value)}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(_, payload) => formatFullDate(payload?.[0]?.payload?.date, granularity)}
                  formatter={(value, name, item) => (
                    <div className="flex flex-1 items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-muted-foreground">
                        {chartConfig[name as CategoryKey]?.label ?? name}
                      </span>
                      <span className="ml-auto font-mono font-medium tabular-nums text-foreground">
                        {formatMoney(Number(value))}
                      </span>
                    </div>
                  )}
                />
              }
            />
            {visibleCategories.map((key) => (
              <Area
                key={key}
                dataKey={key}
                type="monotone"
                stackId="sales"
                stroke={`var(--color-${key})`}
                fill={`url(#fill-${key})`}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ChartContainer>
      ) : (
        <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-black/10 text-base text-muted-foreground">
          No sales in this period
        </div>
      )}
    </div>
  )
}

export default function DashboardEventShow({
  event,
  sales,
  sales_series,
  sales_from,
  sales_to,
  sales_granularity,
  landing_url,
}: DashboardEventShowProps) {
  const isFundraiser = event.page_kind === 'fundraiser'

  return (
    <>
      <Head title={`Overview — ${event.title}`} />
      <CampaignWorkspace
        event={{ ...event, landing_url }}
        title="Overview"
        active="overview"
        actions={(
          <>
            {event.status === 'published' && (
              <Button variant="outline" size="sm" asChild>
                <a href={`/events/${event.slug}`} target="_blank" rel="noopener noreferrer">View</a>
              </Button>
            )}
            {event.status === 'published' && !isFundraiser && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/dashboard/events/${event.id}/check_ins`}>Check in</Link>
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link href={`/dashboard/events/${event.id}/edit`}>Edit</Link>
            </Button>
          </>
        )}
      >
        <div className="max-w-5xl">
          <div className={`grid grid-cols-1 gap-4 ${isFundraiser ? 'sm:grid-cols-1 sm:max-w-xs' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
            {!isFundraiser && (
              <>
                <SalesCard
                  label="Tickets"
                  icon={<Ticket className="size-4" />}
                  category={sales.tickets}
                  unitLabel="tickets sold"
                />
                <SalesCard
                  label="Sponsors"
                  icon={<Megaphone className="size-4" />}
                  category={sales.sponsorships}
                  unitLabel="sponsor units"
                />
                <SalesCard
                  label="Ads"
                  icon={<Newspaper className="size-4" />}
                  category={sales.ads}
                  unitLabel="ad units"
                />
              </>
            )}
            <SalesCard
              label="Donations"
              icon={<Heart className="size-4" />}
              category={sales.donations}
              unitLabel="gifts"
            />
          </div>

          <SalesTrendChart
            series={sales_series}
            fromDate={sales_from}
            toDate={sales_to}
            granularity={sales_granularity}
            isFundraiser={isFundraiser}
          />
        </div>
      </CampaignWorkspace>
    </>
  )
}

DashboardEventShow.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
